import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { DragSource, DropTarget } from 'react-dnd-compat';
import { throttle } from 'underscore';
import styled from 'foundations-theming/utils';
import emptyFunction from 'react-utils/emptyFunction';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Button } from 'foundations-components/transitional/components/Button';
import { TableHoverCell } from 'foundations-components/transitional/components/Table';
import { DragHandle } from 'foundations-components/transitional/components/DragHandle';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import ErrorIcon from 'ContentComponents/components/cmv2/ErrorIcon';
import OccurrencesTableCell from './OccurrencesTableCell';
import { View } from 'foundations-components/components/View';
import { addValidationError, removeValidationError } from 'ContentData/actions/ValidationErrors';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { DuplicateIcon } from 'foundations-assets/icons/components/DuplicateIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { FavoriteIcon } from 'foundations-assets/icons/components/FavoriteIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapDispatchToProps = {
  addValidationError,
  removeValidationError
};
const OCCURRENCE_DRAG_TYPE = 'OCCURRENCE';
let draggingIndex = null;
let draggingFieldId = null;

/**
 * DragSource
 */
const OccurrenceDragSource = DragSource(OCCURRENCE_DRAG_TYPE, {
  beginDrag(props) {
    draggingIndex = props.index;
    draggingFieldId = props.fieldId;
    return {
      index: props.index,
      hasValidationError: props.hasValidationError
    };
  },
  canDrag(props) {
    return !(props.readOnly || props.isPreview);
  },
  isDragging(props) {
    return props.index === draggingIndex && props.fieldId === draggingFieldId;
  },
  endDrag(props) {
    props.onDropOccurrence();
    draggingIndex = null;
    draggingFieldId = null;
  }
}, monitor => ({
  isDragging: monitor.isDragging()
}));

/**
 * DropTarget
 */
const OccurrenceDropTarget = DropTarget(OCCURRENCE_DRAG_TYPE, {
  hover: throttle((props, monitor) => {
    draggingIndex = props.index;
    const item = monitor.getItem();
    if (!item || props.index === item.index) {
      return;
    }
    if (item.hasValidationError && !props.hasValidationError) {
      props.removeValidationError([...props.fieldKey, item.index]);
      props.addValidationError([...props.fieldKey, props.index]);
    }
    if (!item.hasValidationError && props.hasValidationError) {
      props.removeValidationError([...props.fieldKey, props.index]);
      props.addValidationError([...props.fieldKey, item.index]);
    }
    props.onDragOccurrence(item.index, props.index);
    monitor.getItem().index = props.index;
  }, 250)
}, () => ({}));
const OccurrenceLabelWrapper = styled.span.withConfig({
  displayName: "OccurrencesTableRow__OccurrenceLabelWrapper",
  componentId: "sc-1hmbnnw-0"
})(["cursor:pointer;display:table;position:relative;"]);
const i18nKey = key => `ContentComponents.cmv2.${key}`;
class OccurrenceTableRow extends Component {
  renderCell() {
    const {
      cloned,
      featured,
      isDragging,
      isPreview,
      hasValidationError,
      onEditOccurrence,
      readOnly,
      reorderableOnly,
      value
    } = this.props;
    if (readOnly) {
      return /*#__PURE__*/_jsx(OccurrencesTableCell, {
        isReadOnly: true,
        children: /*#__PURE__*/_jsx(TruncateString, {
          children: value
        })
      });
    }
    const errorIcon = hasValidationError ? /*#__PURE__*/_jsx(Tooltip, {
      placement: "top right",
      title: unescapedText(i18nKey('errorIcon.editMessage')),
      children: /*#__PURE__*/_jsx(View, {
        as: "span",
        marginInlineEnd: "200",
        children: /*#__PURE__*/_jsx(ErrorIcon, {})
      })
    }) : null;
    const featuredBadge = featured ? /*#__PURE__*/_jsx(Badge, {
      use: "calypso",
      marginInlineStart: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('occurrence.featured')
      })
    }) : null;
    return /*#__PURE__*/_jsx(DragHandle, {
      children: /*#__PURE__*/_jsx(OccurrencesTableCell, {
        isCloned: cloned,
        isDragging: isDragging,
        isPreview: isPreview,
        paddingInlineStart: "600",
        children: /*#__PURE__*/_jsx(TableHoverCell, {
          as: "div",
          hoverContent: !reorderableOnly && this.renderActions(),
          children: /*#__PURE__*/_jsxs(TFlex, {
            align: "center",
            children: [errorIcon, /*#__PURE__*/_jsx(OccurrenceLabelWrapper, {
              onClick: onEditOccurrence,
              children: value
            }), featuredBadge]
          })
        })
      })
    });
  }
  renderActions() {
    const {
      deletable,
      featured,
      featuredEnabled,
      hasMaxFeaturedOccurrences,
      hasMaxOccurrences,
      isDragging,
      isPreview,
      onDeleteOccurrence,
      onDuplicateOccurrence,
      onEditOccurrence,
      toggleFeatureOccurrence,
      fieldKey
    } = this.props;
    if (isPreview) {
      return null;
    }
    const deleteButton = deletable && !isDragging ? /*#__PURE__*/_jsx(Tooltip, {
      title: unescapedText(i18nKey('occurrence.actions.delete')),
      children: /*#__PURE__*/_jsx(Button, {
        "aria-label": "Delete",
        "data-selenium": `${fieldKey.join('.')}:occurrences:table:row:delete`,
        onClick: onDeleteOccurrence,
        use: "link",
        marginInlineStart: "200",
        children: /*#__PURE__*/_jsx(DeleteIcon, {
          size: "200",
          color: "icon-interactive-default"
        })
      })
    }) : null;
    const duplicateButton = !hasMaxOccurrences ? /*#__PURE__*/_jsx(Tooltip, {
      title: unescapedText(i18nKey('occurrence.actions.clone')),
      children: /*#__PURE__*/_jsx(Button, {
        "aria-label": "Clone",
        "data-selenium": `${fieldKey.join('.')}:occurrences:table:row:clone`,
        onClick: onDuplicateOccurrence,
        size: "extra-small",
        use: "link",
        children: /*#__PURE__*/_jsx(DuplicateIcon, {
          size: "200",
          color: "icon-interactive-default"
        })
      })
    }) : null;
    const canFeatureOccurrence = !featured && !hasMaxFeaturedOccurrences;
    const featureButton = featuredEnabled && (featured || canFeatureOccurrence) && !isDragging ? /*#__PURE__*/_jsx(Tooltip, {
      title: featured ? unescapedText(i18nKey('occurrence.actions.unFeature')) : unescapedText(i18nKey('occurrence.actions.feature')),
      children: /*#__PURE__*/_jsx(Button, {
        "data-selenium": `${fieldKey.join('.')}:occurrences:table:row:feat`,
        onClick: toggleFeatureOccurrence,
        use: "link",
        marginInlineStart: "200",
        children: /*#__PURE__*/_jsx(FavoriteIcon, {
          size: "200",
          color: "icon-interactive-default"
        })
      })
    }) : null;
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: unescapedText(i18nKey('occurrence.actions.edit')),
        children: /*#__PURE__*/_jsx(Button, {
          "aria-label": "Edit",
          "data-selenium": `${fieldKey.join('.')}:occurrences:table:row:edit`,
          onClick: onEditOccurrence,
          size: "extra-small",
          use: "link",
          children: /*#__PURE__*/_jsx(EditIcon, {
            size: "200",
            color: "icon-interactive-default"
          })
        })
      }), duplicateButton, featureButton, deleteButton]
    });
  }
  render() {
    const {
      connectDragSource,
      connectDropTarget,
      fieldKey
    } = this.props;
    return compose(connectDragSource, connectDropTarget)( /*#__PURE__*/_jsx("tr", {
      "data-selenium": `${fieldKey.join('.')}:occurrences:table:row`,
      children: this.renderCell()
    }));
  }
}
OccurrenceTableRow.defaultProps = {
  fieldKey: [],
  isPreview: false,
  onDeleteOccurrence: emptyFunction,
  onEditOccurrence: emptyFunction,
  onDragOccurrence: emptyFunction,
  onDropOccurrence: emptyFunction,
  readOnly: false,
  value: null
};
export default compose(connect(undefined, mapDispatchToProps), OccurrenceDragSource, OccurrenceDropTarget)(OccurrenceTableRow);